import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import LanguageSelector from '../components/LanguageSelector';
import { API_ENDPOINTS, MODULE_URL_OVERRIDES } from '../utils/config';
import {
    ModuleSummary,
    isGordianAdmin,
    autoEnterTarget,
    getSafeRedirectUrl,
    pendingRedirect,
    getIconSvg,
} from './moduleSelectionUtils';
import './ModuleSelectionPage.css';

/**
 * Product decision: users can navigate to the portal to view available modules.
 * For customers (non-admin users), only the RFQ module is active by default (or invoice-management for B2C).
 * Locked modules display an "Upgrade Plan" badge and direct users to contact Gordian to get access.
 * Gordian / super admins retain full access to all available modules.
 */
const AUTO_ENTER_MODULE_FOR_USERS = false;

const ModuleSelectionPage = () => {
    const { user, token, logout } = useAuth();
    const { t } = useTranslation();
    const [modules, setModules] = useState<ModuleSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(false);
    const [apiIsAdmin, setApiIsAdmin] = useState<boolean | null>(null);

    useEffect(() => {
        // Check if there's a redirect URL from another module
        const redirectUrl = pendingRedirect();

        if (redirectUrl && token) {
            // If we came with a redirect URL, go back there with token
            const separator = redirectUrl.includes('?') ? '&' : '?';
            window.location.href = `${redirectUrl}${separator}token=${token}`;
            return;
        }

        fetchModules();
    }, [token, user]);

    const isAdmin = apiIsAdmin ?? isGordianAdmin(user);

    const isModuleEnabled = (module: ModuleSummary): boolean => {
        if (module.status !== 'active') {
            return false;
        }
        if (isAdmin) {
            return true;
        }
        if ((user?.account_type || '').toLowerCase() === 'b2c') {
            return module.id === 'invoice-management';
        }
        return module.id === 'rfq';
    };

    const getModuleName = (module: ModuleSummary): string => {
        const key = `common.module_${module.id.replace(/-/g, '_')}_name`;
        const translated = t(key);
        return translated !== key ? translated : (module.name || module.id);
    };

    const getModuleDescription = (module: ModuleSummary): string => {
        const key = `common.module_${module.id.replace(/-/g, '_')}_desc`;
        const translated = t(key);
        return translated !== key ? translated : (module.description || '');
    };

    const fetchModules = async () => {
        setFetchError(false);
        try {
            const response = await fetch(API_ENDPOINTS.MODULES, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            if (data.success) {
                const fetched: ModuleSummary[] = data.data.modules;
                setModules(fetched);

                const backendAdmin = Boolean(data.data?.user?.is_admin);
                if (data.data?.user && typeof data.data.user.is_admin === 'boolean') {
                    setApiIsAdmin(backendAdmin);
                }

                const effectiveAdmin = backendAdmin || isGordianAdmin(user);

                if (AUTO_ENTER_MODULE_FOR_USERS && !effectiveAdmin) {
                    const target = autoEnterTarget(fetched);
                    if (target) {
                        openModule(target);
                        return;
                    }
                }
            } else {
                setFetchError(true);
            }
        } catch (err) {
            console.error('Failed to fetch modules:', err);
            setFetchError(true);
        }
        setLoading(false);
    };

    /** Jump into a module, handing over the portal token. */
    const openModule = (module: ModuleSummary) => {
        if (!isModuleEnabled(module)) {
            return;
        }
        // Non-admins must have a valid redirect_url provided by backend
        if (!isAdmin && !module.redirect_url) {
            return;
        }
        // Use frontend env override if available, otherwise fall back to backend URL
        const rawUrl = MODULE_URL_OVERRIDES[module.id] || module.redirect_url;
        if (!rawUrl) {
            return;
        }
        const safe = getSafeRedirectUrl(rawUrl);
        if (!safe) {
            return;
        }
        // Pre-existing Gordian SSO handoff pattern: receiving modules (e.g., RFQ-Frontend AuthContext)
        // consume ?token= on initial mount, persist it, and sanitize the query parameter.
        const separator = safe.includes('?') ? '&' : '?';
        window.location.href = `${safe}${separator}token=${token}`;
    };

    const handleModuleClick = (module: ModuleSummary) => {
        if (!isModuleEnabled(module)) {
            return;
        }

        openModule(module);
    };

    const handleLogout = () => {
        logout();
        window.location.href = '/signin';
    };

    const handingOff = loading && (
        Boolean(pendingRedirect()) || (AUTO_ENTER_MODULE_FOR_USERS && !isAdmin)
    );

    if (handingOff) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="module-page">
            {/* Animated Background */}
            <div className="animated-background">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <div className="floating-shape shape-3"></div>
                <div className="floating-shape shape-4"></div>
                <div className="floating-shape shape-5"></div>
            </div>

            <header className="module-header">
                <div className="header-left">
                    <div className="header-logo">
                        <img src="/favicon-32x32-white.png" alt="Logo" className="logo-icon" />
                        <span>{t('common.portal_title')}</span>
                    </div>
                    <div className="header-separator" aria-hidden="true" />
                    <LanguageSelector align="left" />
                </div>
                <div className="header-actions">
                    <button
                        type="button"
                        className="btn-logout"
                        onClick={handleLogout}
                        title={t('common.logout')}
                    >
                        <svg
                            className="logout-icon"
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                            <polyline points="16 17 21 12 16 7" />
                            <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                        <span>{t('common.logout')}</span>
                    </button>
                </div>
            </header>

            <main className="module-content">
                <div className="welcome-section">
                    <h1>{t('common.modules_welcome', { name: user?.name?.split(' ')[0] || '' })}</h1>
                    <p>{t('common.modules_select')}</p>
                </div>

                {loading ? (
                    <div className="loading">
                        <div className="spinner"></div>
                        <p>{t('common.modules_loading')}</p>
                    </div>
                ) : fetchError && modules.length === 0 ? (
                    <div className="modules-error-state">
                        <div className="modules-error-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                        </div>
                        <p className="modules-error-text">{t('common.modules_error')}</p>
                        <button
                            type="button"
                            className="btn-retry"
                            onClick={() => {
                                setLoading(true);
                                fetchModules();
                            }}
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
                            </svg>
                            <span>{t('common.retry')}</span>
                        </button>
                    </div>
                ) : (
                    <div className="modules-grid">
                        {modules.map((module: ModuleSummary) => {
                            const enabled = isModuleEnabled(module);
                            const moduleName = getModuleName(module);
                            const statusLabel = enabled ? '' : t('common.upgrade_plan');
                            const cardAriaLabel = enabled ? moduleName : `${moduleName} (${statusLabel})`;

                            return (
                                <div
                                    key={module.id}
                                    className={`module-card ${!enabled ? 'disabled' : ''}`}
                                    onClick={enabled ? () => handleModuleClick(module) : undefined}
                                    onKeyDown={enabled ? (e) => {
                                        if (e.key === ' ' || e.key === 'Spacebar') {
                                            e.preventDefault();
                                            handleModuleClick(module);
                                        } else if (e.key === 'Enter') {
                                            handleModuleClick(module);
                                        }
                                    } : undefined}
                                    style={{ '--module-color': module.color } as React.CSSProperties}
                                    role={enabled ? 'button' : undefined}
                                    tabIndex={enabled ? 0 : -1}
                                    aria-disabled={!enabled}
                                    aria-label={cardAriaLabel}
                                >
                                    <div className="module-icon" style={{ background: module.color }}>
                                        {getIconSvg(module.icon)}
                                    </div>
                                    <div className="module-info">
                                        <h3>{getModuleName(module)}</h3>
                                        <p className="module-desc">{getModuleDescription(module)}</p>
                                        {!enabled && (
                                            <div className="module-upgrade-note">
                                                <a
                                                    href={`mailto:info@gordiananalytics.com?subject=${encodeURIComponent(t('common.module_access_subject', { module: moduleName }))}`}
                                                    className="module-contact-link"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {t('common.module_contact_us')} &rarr;
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                    {!enabled && (
                                        <span className="module-badge upgrade-plan">
                                            {t('common.upgrade_plan')}
                                        </span>
                                    )}
                                    {enabled && (
                                        <div className="module-arrow">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="5" y1="12" x2="19" y2="12" />
                                                <polyline points="12 5 19 12 12 19" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </main>

            <footer className="module-footer">
                <p>{t('common.modules_footer')}</p>
            </footer>
        </div>
    );
};

export default ModuleSelectionPage;
